import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';

import {
  createMilestone,
  deleteMilestone,
  updateMilestone,
  type RockMilestone,
  type UpdateMilestoneRequest,
} from '@/api/eos/rocks.api';
import { Button } from '@/components/ui/Button';
import { describeEosError } from '@/lib/eos/errors';
import { useToast } from '@/hooks/useToast';

/**
 * Milestones section of the rock detail drawer (Phase H — Rocks depth).
 *
 * Each row carries a complete checkbox (toggles `completedAt`), a due-date
 * editor, and a health toggle that flips an open milestone between on_track
 * and off_track. Off-track rows get a subtle warning tint. Completed
 * milestones show a static "Done" chip. Every mutation invalidates the
 * shared `['eos','rocks']` query so the drawer re-renders off fresh data.
 */

const INPUT_CLS =
  'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

/** ISO datetime → the `YYYY-MM-DD` a `<input type="date">` wants. */
function toDateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

/** `<input type="date">` value → an ISO datetime the API stores, or `null`. */
function toIsoDateTime(date: string): string | null {
  return date ? `${date}T00:00:00.000Z` : null;
}

export function RockMilestones({
  rockId,
  milestones,
  canEdit,
}: {
  rockId: string;
  milestones: RockMilestone[];
  canEdit: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState('');

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'rocks'] });
  }

  const addMutation = useMutation({
    mutationFn: ({ title, dueDate }: { title: string; dueDate: string | null }) =>
      createMilestone(rockId, { title, dueDate }),
    onSuccess: () => {
      setNewTitle('');
      setNewDue('');
      invalidate();
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not add the milestone.'), { variant: 'danger' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ milestoneId, body }: { milestoneId: string; body: UpdateMilestoneRequest }) =>
      updateMilestone(rockId, milestoneId, body),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not update the milestone.'), { variant: 'danger' });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (milestoneId: string) => deleteMilestone(rockId, milestoneId),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not delete the milestone.'), { variant: 'danger' });
    },
  });

  const ordered = [...milestones].sort((a, b) => a.order - b.order);
  const doneCount = ordered.filter((m) => m.completedAt !== null).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Milestones</h3>
        {ordered.length > 0 ? (
          <span className="text-[11px] text-kw-muted">
            {doneCount}/{ordered.length} done
          </span>
        ) : null}
      </div>

      {ordered.length === 0 ? (
        <p className="text-[12px] text-kw-muted">No milestones yet.</p>
      ) : (
        <ul className="space-y-1">
          {ordered.map((m) => {
            const completed = m.completedAt !== null;
            const offTrack = !completed && m.status === 'off_track';
            return (
              <li
                key={m.id}
                className={`flex flex-wrap items-center gap-2 rounded-kw-md px-1.5 py-1 ${
                  offTrack ? 'bg-kw-warning-bg/60' : ''
                }`}
              >
                <input
                  type="checkbox"
                  aria-label={`Complete milestone ${m.title}`}
                  checked={completed}
                  disabled={!canEdit || updateMutation.isPending}
                  onChange={(e) => {
                    updateMutation.mutate({
                      milestoneId: m.id,
                      body: { completedAt: e.target.checked ? new Date().toISOString() : null },
                    });
                  }}
                  className="size-4 shrink-0 rounded border-kw-border text-kw-cerulean focus:ring-kw-cerulean/40"
                />
                <span
                  className={`min-w-0 flex-1 text-[13px] ${
                    completed ? 'text-kw-muted line-through' : 'text-kw-shark'
                  }`}
                >
                  {m.title}
                </span>

                {canEdit ? (
                  <input
                    type="date"
                    aria-label={`Due date for milestone ${m.title}`}
                    value={toDateInput(m.dueDate)}
                    onChange={(e) => {
                      updateMutation.mutate({
                        milestoneId: m.id,
                        body: { dueDate: toIsoDateTime(e.target.value) },
                      });
                    }}
                    className={`${INPUT_CLS} w-[9.5rem]`}
                  />
                ) : m.dueDate ? (
                  <span className="text-[11px] text-kw-muted">{m.dueDate.slice(0, 10)}</span>
                ) : null}

                {completed ? (
                  <span className="rounded-kw-full bg-kw-cerulean-light px-2 py-0.5 text-[11px] font-medium text-kw-cerulean-dark">
                    Done
                  </span>
                ) : canEdit ? (
                  <button
                    type="button"
                    aria-label={`Toggle status for milestone ${m.title}`}
                    title={
                      offTrack ? 'Off track — click to mark on track' : 'On track — click to flag off track'
                    }
                    onClick={() => {
                      updateMutation.mutate({
                        milestoneId: m.id,
                        body: { status: offTrack ? 'on_track' : 'off_track' },
                      });
                    }}
                    className={`rounded-kw-full px-2 py-0.5 text-[11px] font-medium transition-opacity hover:opacity-80 ${
                      offTrack
                        ? 'bg-kw-warning-bg text-kw-warning-text'
                        : 'bg-kw-emerald-light text-kw-tag-text'
                    }`}
                  >
                    {offTrack ? 'Off track' : 'On track'}
                  </button>
                ) : (
                  <span
                    className={`rounded-kw-full px-2 py-0.5 text-[11px] font-medium ${
                      offTrack
                        ? 'bg-kw-warning-bg text-kw-warning-text'
                        : 'bg-kw-emerald-light text-kw-tag-text'
                    }`}
                  >
                    {offTrack ? 'Off track' : 'On track'}
                  </span>
                )}

                {canEdit ? (
                  <button
                    type="button"
                    aria-label={`Delete milestone ${m.title}`}
                    onClick={() => {
                      removeMutation.mutate(m.id);
                    }}
                    className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {canEdit ? (
        <form
          onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const title = newTitle.trim();
            if (title) addMutation.mutate({ title, dueDate: toIsoDateTime(newDue) });
          }}
          className="flex flex-wrap items-center gap-2"
        >
          <input
            value={newTitle}
            onChange={(e) => {
              setNewTitle(e.target.value);
            }}
            placeholder="Add a milestone…"
            aria-label="New milestone title"
            className={`${INPUT_CLS} min-w-0 flex-1`}
          />
          <input
            type="date"
            value={newDue}
            onChange={(e) => {
              setNewDue(e.target.value);
            }}
            aria-label="New milestone due date"
            className={`${INPUT_CLS} w-[9.5rem]`}
          />
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            isLoading={addMutation.isPending}
            disabled={!newTitle.trim()}
          >
            Add
          </Button>
        </form>
      ) : null}
    </div>
  );
}
